import React, { useEffect, useRef, useState } from 'react';
import 'react-phone-number-input/style.css';

import moment from 'moment-timezone';

import Appearance from 'styles/Appearance.js';
import PhoneInput from 'react-phone-number-input';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import ProgressBar from 'views/ProgressBar.js';
import Utils from 'files/Utils.js';

const TextField = React.forwardRef(({
    append, appendContent, autoCapitalize, autoComplete, autoCorrect, className, containerStyle, expandWithText, fieldStyle, format, focused, icon, iconStyle, insetLabel, insetLabelStyle, insetContainerStyle, isSecure, loading, onChange, onBlur, onKeyDown, onClick, onValidate, placeholder, prepend, prependContent, spellCheck, useDelay, value
}, ref) => {

    const phoneInputRef = useRef(null);

    const [active, setActive] = useState(focused);
    const [countryCode, setCountryCode] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [textTimeout, setTextTimeout] = useState(false);
    const [text, setText] = useState(value !== null && value !== undefined ? value : '');

    const onFieldClick = () => {
        setActive(true);
        if(typeof(onClick) === 'function') {
            onClick();
        }
    }

    const onFieldBlur = () => {
        setActive(false);
        if(typeof(onBlur) === 'function') {
            onBlur();
        }
    }

    const onPhoneInputTextChange = text => {
        try {

            // clear previous timeout if present
            if(textTimeout) {
                clearTimeout(textTimeout);
            }

            // determine if a phone number content validation is needed
            if(text && text.length > 0) {

                // prepare phone number util instance and parse phone number value
                let instance = PhoneNumberUtil.getInstance();
                let result = instance.parseAndKeepRawInput(text);

                // format phone number with country code prefix
                instance.format(result, PhoneNumberFormat.INTERNATIONAL);
            }

            // remove error message from previous input
            setErrorMessage(null);

            // update state and notify listeners of text change
            setText(text);
            setTextTimeout(setTimeout(() => {
                if(typeof(onChange) === 'function') {
                    onChange(text);
                }
            }, useDelay === true ? 250 : 0));

        } catch(e) {
            console.error(e.message);
            let error = formatPhoneUtilError(e);
            setErrorMessage(error.message);
        }
    }

    const onTextChange = evt => {

        // clear previous timeout if present
        if(textTimeout) {
            clearTimeout(textTimeout);
        }

        let newText = evt.target.value.replace(prepend || '', '').replace(append || '', '');
        switch(format) {
            case 'currency':
            newText = newText.replace(/[^\d.-]/g,'');
            break;

            case 'double':
            newText = parseFloat(newText.replace(/[^\d.-]/g,''));
            break;

            case 'integer':
            newText = parseInt(newText.replace(/[^\d.-]/g,''));
            break;

            case 'number':
            newText = newText.replace(/[^\d.-]/g,'');
            break;

            default:
            if(typeof(format) === 'function') {
                newText = format(newText);
            }
        }

        // set text with timeout if applicable
        setText(newText);
        setTextTimeout(setTimeout(() => {
            if(typeof(onChange) === 'function') {
                onChange(newText);
            }
        }, useDelay === true ? 250 : 0));
    }

    const onKeyDownPress = evt => {
        let keyCode = evt.keyCode;
        let value = evt.target.value;
        setTimeout(() => {
            if(typeof(onKeyDown) === 'function') {
                if(keyCode === 8) {
                    onKeyDown('backspace');
                    return;
                }
                onKeyDown(value);
            }
        }, useDelay === false ? 0 : 250);
    }

    const getContent = () => {
        if(format !== 'phone_number') {
            return getField();
        }
        return (
            <div
            className={`dummy-field ${window.theme} ${className || ''}`}
            style={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                justifyContent: 'center',
                minHeight: 48,
                padding: 0,
                position: 'relative',
                ...containerStyle
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    maxWidth: '100%',
                    paddingLeft: 12, 
                    paddingRight: 12
                }}>
                    <PhoneInput
                    autoComplete={'tel'}
                    countryCallingCodeEditable={false}
                    defaultCountry={countryCode}
                    international={true}
                    onChange={onPhoneInputTextChange}
                    placeholder={placeholder}
                    value={text}
                    ref={phoneInputRef}
                    style={{
                        width: '100%'
                    }}/>
                    {appendContent}
                </div>
                {typeof(errorMessage) === 'string' && (
                    <div style={{
                        borderTop: `1px solid ${Appearance.colors.softBorder()}`,
                        padding: '8px 12px 8px 12px',
                        textAlign: 'left'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.subTitle(),
                            color: Appearance.colors.red,
                        }}>{errorMessage}</span>
                    </div>
                )}
            </div>
        )
    }

    const getField = () => {
        return (
            <div
            className={`dummy-field ${window.theme} ${className || ''}`}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
                height: expandWithText ? 'auto' : 48,
                minHeight: 48,
                position: 'relative',
                ...containerStyle
            }}>
                {getIcon(icon) && (
                    <div style={{
                        paddingRight: 12
                    }}>
                        <i
                        className={getIcon(icon)}
                        style={{
                            color: Appearance.colors.icon(),
                            fontSize: 16,
                            ...iconStyle
                        }}/>
                    </div>
                )}
                {insetLabel && (
                    <div style={{
                        borderRight: `1px solid ${Appearance.colors.softBorder()}`,
                        marginRight: 12,
                        paddingRight: 12,
                        ...insetContainerStyle
                    }}>
                        <span style={{
                            color: Appearance.colors.subText(),
                            fontSize: 12,
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            ...insetLabelStyle
                        }}>{insetLabel}</span>
                    </div>
                )}
                {getFieldComponent()}
                <div style={{
                    bottom: 0,
                    height: 2,
                    left: 0,
                    position: 'absolute',
                    right: 0
                }}>
                    <div style={{
                        borderRadius: 2,
                        display: loading ? 'block' : 'none',
                        height: 2,
                        overflow: 'hidden'
                    }}>
                        <ProgressBar />
                    </div>
                </div>
                {appendContent}
                {typeof(onValidate) === 'function' && onValidate(text) && (
                    <img
                    src={'images/checkmark-small-green.png'}
                    style={{
                        height: 15,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 15
                    }} />
                )}
            </div>
        )
    }

    const getFieldComponent = () => {
        if(typeof(onClick) === 'function') {
            return (
                <span
                onClick={onFieldClick}
                className={'cursor-pointer'}
                style={{
                    backgroundColor: Appearance.colors.transparent,
                    borderWidth: 0,
                    color: value ? Appearance.colors.text() : Appearance.colors.subText(),
                    flexGrow: 1,
                    fontFamily: '"Open Sans", sans-serif',
                    fontSize: 16,
                    fontWeight: 400,
                    ...fieldStyle
                }}>{value || placeholder}</span>
            )
        }
        if(isSecure || expandWithText !== true) {
            return (
                <input
                autoCapitalize={autoCapitalize === false ? 'off' : autoCapitalize}
                autoComplete={autoComplete || 'off'}
                autoCorrect={autoCorrect === false ? 'off':'on'}
                className={`dummy-field-input ${window.theme}`}
                inputMode={getInputMode()}
                onBlur={onFieldBlur}
                onChange={onTextChange}
                onClick={onFieldClick}
                onKeyDown={onKeyDownPress}
                placeholder={placeholder || ''}
                ref={ref}
                spellCheck={spellCheck === false ? 'false' : 'true'}
                type={isSecure ? 'password' : 'text'}
                value={(prepend || '') + (text || '') + (append || '')}
                style={{
                    backgroundColor: Appearance.colors.transparent,
                    color: Appearance.colors.text(),
                    border: 'none',
                    fontFamily: '"Open Sans", sans-serif',
                    fontSize: 16,
                    fontWeight: 400,
                    height: 'auto',
                    width: '100%',
                    ...fieldStyle
                }} />
            )
        }

        return (
            <textarea
            autoCapitalize={autoCapitalize === false ? 'off' : autoCapitalize}
            autoComplete={autoComplete || 'off'}
            autoCorrect={autoCorrect === false ? 'off':'on'}
            className={`dummy-field-input ${window.theme}`}
            onBlur={onFieldBlur}
            onClick={onFieldClick}
            onChange={onTextChange}
            onKeyDown={onKeyDownPress}
            placeholder={placeholder || ''}
            ref={ref}
            spellCheck={spellCheck === false ? 'false' : 'true'}
            type={isSecure === true ? 'password' : 'text'}
            value={(prepend || '') + (text || '') + (append || '')}
            style={{
                backgroundColor: Appearance.colors.transparent,
                border: 'none',
                color: Appearance.colors.text(),
                fontFamily: '"Open Sans", sans-serif',
                fontSize: 16,
                fontWeight: 400,
                height: 'auto',
                minHeight: 125,
                paddingTop: expandWithText ? 14 : 14,
                resize: 'none',
                verticalAlign: 'middle',
                width: '100%',
                ...fieldStyle
            }} />
        )
    }

    const getInputMode = () => {
        if([ 'integer' ].includes(format)) {
            return 'numeric';
        }
        if([ 'double', 'integer', 'number', 'phone_number' ].includes(format)) {
            return 'decimal';
        }
        return null;
    }

    const setupFormat = () => {
        try {

            // prevent moving forward if phone number formatting is not required
            if(format !== 'phone_number' || phoneInputRef.current === null) {
                return;
            }

            // determine country code using browser locale
            let locale = new Intl.Locale(navigator.language);
            setCountryCode(locale.region.toUpperCase());

            // prepare fallback timezone values
            let timezone = moment.tz.guess();
            let tz_countries = moment.tz.zone(timezone).countries();
            let tz = tz_countries && tz_countries.length > 0 ? tz_countries[0].toLowerCase() : null;

            // set default country using device timezone if no phone number value was provided
            if(!value) {
                setCountryCode(tz && tz.toUpperCase());
                return;
            }

            // prepare phone number util instance and parse phone number value
            let instance = PhoneNumberUtil.getInstance();
            let result = instance.parseAndKeepRawInput(value, locale || 'US');

            // determine which country to select using the region code for the result
            let country = instance.getRegionCodeForNumber(result);
            if(country) {
                setCountryCode(country.toUpperCase());
            }
            
            // format phone number with country code prefix
            let formatted = instance.format(result, PhoneNumberFormat.INTERNATIONAL);
            setText(formatted);
            setErrorMessage(null);

        } catch(e) {
            
            // strange error occurs intermitently and appears to be package related
            if(e.message.includes('a.toUpperCase') === true) {
                return;
            }
            
            // analyze relevant error and format for client
            console.error(e.message);
            let error = formatPhoneUtilError(e);
            setErrorMessage(error.message);
        }
    }

    useEffect(() => {
        setupFormat();
    }, [format]);

    useEffect(() => {
        setActive(focused);
    }, [focused])

    useEffect(() => {
        setText(value || '');
    }, [value]);

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            width: '100%'
        }}>
            {prependContent}
            {getContent()}
        </div>
    )
})

export default TextField;

export const TextFieldContainer = React.forwardRef(({ containerStyle, fieldStyle, icon, iconStyle, loading, onChange, onClick, value }, ref) => {

    const [active, setActive] = useState(false);

    const onFieldClick = async () => {
        try {
            setActive(true);
            if(typeof(onClick) === 'function') {
                onClick();
            }
            await Utils.sleep(1);
            setActive(false);

        } catch(e) {
            console.error(e.message);
        }
    }

    const getContent = () => {
        return (
            <div
            className={`dummy-field ${window.theme}`}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 1,
                height: 48,
                minHeight: 48,
                position: 'relative',
                ...containerStyle
            }}>
                {getIcon(icon) && (
                    <div style={{
                        paddingRight: 12
                    }}>
                        <i
                        className={getIcon(icon)}
                        style={{
                            color: Appearance.colors.icon(),
                            fontSize: 16,
                            ...iconStyle
                        }}/>
                    </div>
                )}
                <span
                onClick={onFieldClick}
                className={'cursor-pointer'}
                style={{
                    backgroundColor: Appearance.colors.transparent,
                    borderWidth: 0,
                    color: value ? Appearance.colors.text() : Appearance.colors.subText(),
                    flexGrow: 1,
                    fontFamily: '"Open Sans", sans-serif',
                    fontSize: 16,
                    fontWeight: 400,
                    ...fieldStyle
                }}>{value}</span>
                <div style={{
                    bottom: 0,
                    height: 2,
                    left: 0,
                    position: 'absolute',
                    right: 0
                }}>
                    <div style={{
                        borderRadius: 2,
                        display: loading ? 'block' : 'none',
                        height: 2,
                        overflow: 'hidden'
                    }}>
                        <ProgressBar />
                    </div>
                </div>
            </div>
        )
    }

    return getContent();
})

export const formatPhoneUtilError = e => {
    switch(e.message) {
        case 'Invalid country calling code':
        return new Error('Invalid country code');

        case 'The string supplied did not seem to be a phone number':
        return new Error('Phone number is invalid');

        case 'The string supplied is too short to be a phone number':
        return new Error('Phone number is too short');

        case 'The string supplied is too long to be a phone number':
        return new Error('Phone number is too long');

        default:
        return e;
    }
}

export const getIcon = (icon) => {
    switch(icon) {

        case 'user':
        return 'fas fa-user';

        case 'spouse':
        return 'fas fa-house-user';

        case 'user-secret':
        return 'fas fa-user-secret';

        case 'email':
        return 'fas fa-envelope';

        case 'phone':
        return 'fas fa-phone';

        case 'calendar':
        return 'fas fa-calendar-alt';

        case 'house':
        return 'fas fa-home';

        case 'search':
        return 'fas fa-search';

        case 'lock':
        return 'fas fa-lock';

        case 'location':
        return 'fas fa-location-arrow';

        case 'payment':
        return 'fas fa-money-check-alt';

        case 'asterick':
        return 'fas fa-asterisk';

        case 'route':
        return 'fas fa-route';

        case 'guardian':
        return 'fas fa-user-friends';

        case 'title':
        return 'fas fa-signature';

        case 'description':
        return 'fas fa-stream';

        case 'featured':
        return 'fas fa-hand-holding-heart';

        case 'wifi':
        return 'fas fa-wifi';

        case 'pattern':
        return 'fas fa-th';

        case 'office':
        return 'fas fa-briefcase';

        case 'globe':
        return 'far fa-globe';

        default:
        return;
    }
}
